import { deriveBaselineFromId } from 'common/components/WISDetailsModal/helpers'
import { formatDate } from 'features/RiskManagement/Assessments/Components/formatDate'

import type {
  IWisDetailsModalDetailsSection,
  IWisDetailsModalScenarioProps
} from './types'
import { WisDetailsModalDivider } from './Divider'
import { WisDetailsModalHeading } from './Heading'
import { WisDetailsModalSection } from './Section'
import { WisDetailsModalRoutesList } from './RoutesList'
import { WisDetailsModalRules } from './Rules'

export const WisDetailsModalScenario = ({
  currentState,
  assessment,
  isInAccordion
}: IWisDetailsModalScenarioProps) => {
  const details: IWisDetailsModalDetailsSection['items'] = assessment
    ? [
        { name: 'WIS Name', value: assessment.name },
        { name: 'Status', value: assessment.statusCode },
        { name: 'Saved State', value: currentState.name },
        {
          name: 'Baseline',
          value: deriveBaselineFromId(currentState.baselineId)
        },
        {
          name: 'Date',
          value: formatDate(assessment.dateCreated, 'long')
        },
        { name: 'Author', value: assessment?.author }
      ]
    : [
        { name: 'Name', value: currentState.name },
        {
          name: 'Baseline',
          value: deriveBaselineFromId(currentState.baselineId)
        },
        {
          name: 'Date',
          value: formatDate(currentState.dateCreated, 'long')
        },
        { name: 'Author', value: currentState.author }
      ]

  const sections: Array<IWisDetailsModalDetailsSection> = assessment
    ? [
        {
          title: 'Scope',
          items: [
            {
              name: 'Routes',
              value: (
                <WisDetailsModalRoutesList
                  routes={assessment.assessmentContinuousRoutes}
                />
              )
            },
            {
              name: 'Consequence Area',
              value: assessment.assessmentConsequenceLevels.map(
                (consequence) => (
                  <div key={consequence.name}>{consequence.name}</div>
                )
              )
            },
            {
              name: 'Class Location',
              value: assessment.assessmentClassLocations.map(
                (classLocation) => (
                  <div key={classLocation.name}>{classLocation.name}</div>
                )
              )
            },
            {
              name: 'Risk Level',
              value: assessment.assessmentRiskLevels.map((riskLevel) => (
                <div key={riskLevel.name}>{riskLevel.name}</div>
              ))
            }
          ]
        },
        {
          title: 'Scenarios',
          items: assessment.assessmentScenarios.map((scenario) => ({
            name: scenario.name,
            value: null,
            children: scenario.rules.map((rule, index) => (
              <WisDetailsModalRules
                key={`${rule.attributeName}-${index}`}
                rule={rule}
              />
            ))
          }))
        }
      ].filter((section) => {
        if (section.title === 'Scenarios') {
          return Boolean(section.items.length)
        }

        return true
      })
    : []

  /**
   * Helper to address object stringifying to [object Object] issue
   * by returning undefined if the input is not a string, thereby allow
   * short circuiting to the following values
   */
  const getStringElseUndefined = (node: React.ReactNode) => {
    return typeof node === 'string' ? node : undefined
  }

  return (
    <div>
      {!isInAccordion && (
        <>
          <WisDetailsModalSection
            isInAccordion={isInAccordion}
            items={details}
          />
          <WisDetailsModalDivider />
        </>
      )}
      {sections.map((section, index, array) => {
        // section title and items should always exist based on current implementation
        const indexSectionName =
          getStringElseUndefined(section.title) ??
          getStringElseUndefined(section.items[0]?.name) ??
          'section'

        return (
          <div key={`${indexSectionName}-${index}`}>
            {section.title && (
              <WisDetailsModalHeading isInAccordion={isInAccordion}>
                {section.title}
              </WisDetailsModalHeading>
            )}
            <WisDetailsModalSection
              isInAccordion={isInAccordion}
              items={section.items}
            />
            {index + 1 < array.length && <WisDetailsModalDivider />}
          </div>
        )
      })}
    </div>
  )
}

export default WisDetailsModalScenario
